import { useEffect } from 'react';
import formbricks from '@formbricks/js';

interface UseFormbricksSurveyOptions {
  /** The formbricks trigger/survey ID to fire */
  triggerId: string;
  /** User email for formbricks identification */
  userEmail?: string;
  /** Whether the survey should be triggered (all conditions must be true externally) */
  shouldTrigger: boolean;
  /** Formbricks environment ID (defaults to production) */
  environmentId?: string;
  /** Formbricks app URL */
  appUrl?: string;
}

/**
 * Hook that initializes Formbricks and fires a survey trigger.
 *
 * Usage:
 * ```tsx
 * useFormbricksSurvey({
 *   triggerId: 'two_plus_trigger_en',
 *   userEmail: user?.email,
 *   shouldTrigger: isFreeUser && promptsUsed > 2,
 * });
 * ```
 */
export function useFormbricksSurvey({
  triggerId,
  userEmail,
  shouldTrigger,
  environmentId = 'cmexx4vpt6s1au501thjqs4ij',
  appUrl = 'https://app.formbricks.com',
}: UseFormbricksSurveyOptions) {
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!shouldTrigger) {
      return;
    }

    formbricks
      .setup({ environmentId, appUrl })
      .then(() => {
        formbricks.logout();
        formbricks.setUserId(userEmail || 'DUMMY_EMAIL');
        formbricks.setEmail(userEmail || 'DUMMY_EMAIL');
        formbricks.track(triggerId);
      })
      .catch((err) => {
        console.error('[FormbricksSurvey] Failed to trigger survey:', err);
      });
  }, [shouldTrigger, triggerId, userEmail, environmentId, appUrl]);
}
